import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ArrowDownRight, ArrowUpRight } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { percentChange } from './format';
import { Sparkline } from './Sparkline';

export interface KpiCardProps {
  label: string;
  value: ReactNode;
  /** Compared with the previous period: the change is the only colour on the card. */
  delta?: { current: number; previous: number };
  /** Words after the change ("vs previous 30 days") or a plain hint when there is no delta. */
  hint?: ReactNode;
  icon?: ReactNode;
  /** A small trend line under the number. */
  trend?: number[];
  /** A rise is bad (errors, failures): flips the colours. */
  invert?: boolean;
  href?: string;
  loading?: boolean;
  className?: string;
  testId?: string;
}

/** One number, where it is heading, and against what. */
export function KpiCard({ label, value, delta, hint, icon, trend, invert, href, loading, className, testId }: KpiCardProps) {
  const { t } = useTranslation();
  const change = delta ? percentChange(delta.current, delta.previous) : null;
  const up = (change ?? 0) > 0;
  const good = invert ? !up : up;

  const body = (
    <Card
      className={cn('relative h-full gap-0 px-4 py-4', href && 'transition-colors hover:bg-accent/40', className)}
      data-testid={testId}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-muted-foreground">{label}</p>
        {icon && <span className="text-muted-foreground [&_svg]:size-4">{icon}</span>}
      </div>
      {loading ? (
        <>
          <Skeleton className="mt-3 h-8 w-24" />
          <Skeleton className="mt-2 h-3 w-32" />
        </>
      ) : (
        <>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-heading text-3xl leading-none font-medium tracking-tight tabular-nums">{value}</span>
            {change !== null && Math.round(change) !== 0 && (
              <span className={cn('inline-flex items-center gap-0.5 text-xs font-medium tabular-nums', good ? 'text-success' : 'text-destructive')}>
                {up ? <ArrowUpRight className="size-3.5" /> : <ArrowDownRight className="size-3.5" />}
                {Math.abs(Math.round(change))}%
              </span>
            )}
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">
            {delta && change === null && delta.current === 0 && delta.previous === 0 ? t('ADMIN_SHELL.COMMON.NO_CHANGE') : hint}
          </p>
          {trend && trend.length > 1 && <Sparkline data={trend} className="mt-3 h-8" />}
        </>
      )}
    </Card>
  );

  return href ? (
    <Link to={href} className="block rounded-xl focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none">
      {body}
    </Link>
  ) : (
    body
  );
}
